import { RefObject, useCallback, useEffect, useRef } from 'react';

interface EventListenerSubscription {
  target: EventTarget;
  type: string;
  unsubscribe: () => void;
}

export function useEventListener<K extends keyof GlobalEventHandlersEventMap>(
  targetRef: RefObject<HTMLElement>,
  type: K,
  listener?: (this: GlobalEventHandlers, event: GlobalEventHandlersEventMap[K]) => any,
): void;
export function useEventListener(
  targetRef: RefObject<EventTarget>,
  type: string,
  listener?: EventListenerOrEventListenerObject,
): void;
export function useEventListener(
  targetRef: RefObject<EventTarget>,
  type: string,
  listener?: EventListenerOrEventListenerObject,
) {
  // Stores the parameters of the current event listener subscription, if any.
  const subscriptionRef = useRef<EventListenerSubscription>();

  // Stores the current event listener handler, if any.
  const listenerRef = useRef<EventListenerOrEventListenerObject>();

  // Static handler that dynamically forwards to the current handler.
  const memoizedListener = useCallback((event: Event) => {
    const currentListener = listenerRef.current;
    if (typeof currentListener === 'object') {
      currentListener.handleEvent(event);
    } else if (typeof currentListener === 'function') {
      currentListener(event);
    }
  }, []);

  // Runs every time.
  useEffect(() => {
    const target = targetRef.current;

    // Update the current event handler.
    listenerRef.current = listener;

    // Update the current subscription.
    const subscription = subscriptionRef.current;
    if (subscription) {
      // Do nothing if we still have an event handler, and all parameters are the same.
      if (listener && subscription.target === target && subscription.type === type) {
        return;
      }

      // Unsubscribe, because we lost our event handler, or parameters have changed.
      subscription.unsubscribe();
      subscriptionRef.current = undefined;
    }

    // Do we have anything to subscribe to?
    if (!target || !listener) {
      return;
    }

    // Subscribe.
    target.addEventListener(type, memoizedListener);

    // Save the parameters of the subscription so we can detect changes and unsubscribe later.
    subscriptionRef.current = {
      target,
      type,
      unsubscribe: () => {
        target.removeEventListener(type, memoizedListener);
      },
    };
  });

  // Runs when the component mounts.
  useEffect(() => {
    // Runs when the component unmounts.
    return () => {
      // Clean up the current event listener subscription, if any.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const subscription = subscriptionRef.current;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);
}
